import React, { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Loader2, Percent, Receipt, Landmark, TrendingDown } from "lucide-react";
import { apiClient } from "@/lib/apiClient";
import { useCompany } from "@/context/CompanyContext";

const chf = (v) => `CHF ${Number(v || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pct = (v) => (v == null ? "–" : `${Number(v).toFixed(2)} %`);
const MONTHS = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];
const PROV_STYLE = { twint: "bg-slate-900 text-white", sumup: "bg-amber-100 text-amber-800", payrexx: "bg-sky-100 text-sky-800" };

function Kpi({ icon: Icon, label, value, sub, testid }) {
  return (
    <Card className="p-4 border border-slate-200 bg-white" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-slate-500"><Icon className="h-4 w-4" />{label}</div>
      <div className="mt-1 text-xl font-semibold text-slate-900 tabular-nums">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </Card>
  );
}

export default function PaymentFeesReport() {
  const { company } = useCompany();
  const companyId = company?._id;
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    setLoading(true);
    apiClient.get(`/companies/${companyId}/payout-fees-report`, { params: { year } }).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [companyId, year]);

  const provKeys = useMemo(() => Object.keys(data?.providers || {}).sort(), [data]);
  const maxFees = useMemo(() => Math.max(1, ...(data?.months || []).map((m) => m.fees)), [data]);

  return (
    <div className="space-y-6" data-testid="payment-fees-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Zahlungsgebühren</h1>
          <p className="text-sm text-slate-500">TWINT, SumUp & Payrexx – Gebühren pro Monat im Vergleich zum Umsatz, aus den Auszahlungsberichten.</p>
        </div>
        <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm" data-testid="fees-year-select">
          {[0, 1, 2, 3].map((d) => { const y = new Date().getFullYear() - d; return <option key={y} value={y}>{y}</option>; })}
        </select>
      </div>

      {loading && <div className="text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>}
      {!loading && data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi icon={TrendingDown} label="Gebühren total (Zahlungsdienste)" value={chf(data.total.fees)} sub={`${pct(data.total.fee_pct)} vom Umsatz ${chf(data.total.gross)}`} testid="fees-kpi-total" />
            <Kpi icon={Receipt} label="Umsatz über Zahlungsdienste" value={chf(data.total.gross)} sub={`überwiesen ${chf(data.total.net)}`} testid="fees-kpi-gross" />
            <Kpi icon={Landmark} label={`Konto ${data.account.code} gesamt`} value={chf(data.account.total)} sub={`davon aus Auszahlungen ${chf(data.account.from_payouts)} · übrige Bank-/Zahlungsgebühren ${chf(data.account.other)}`} testid="fees-kpi-account" />
            <Kpi icon={Percent} label="Teuerster Dienst" value={provKeys.length ? (() => { const k = provKeys.slice().sort((a, b) => (data.providers[b].fee_pct || 0) - (data.providers[a].fee_pct || 0))[0]; return `${data.providers[k].label} ${pct(data.providers[k].fee_pct)}`; })() : "–"} sub="effektiver Gebührensatz" testid="fees-kpi-top" />
          </div>

          <Card className="p-4 border border-slate-200 bg-white" data-testid="fees-provider-table">
            <h2 className="text-sm font-semibold text-slate-800 mb-3">Pro Zahlungsdienst ({data.year})</h2>
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500"><tr><th className="text-left py-1">Dienst</th><th className="text-right">Berichte</th><th className="text-right">Zahlungen</th><th className="text-right">Umsatz</th><th className="text-right">Gebühren</th><th className="text-right">Satz</th><th className="text-right">Überwiesen</th></tr></thead>
              <tbody>
                {provKeys.map((k) => { const p = data.providers[k]; return (
                  <tr key={k} className="border-t border-slate-100" data-testid={`fees-provider-${k}`}>
                    <td className="py-1.5"><span className={`rounded px-2 py-0.5 text-xs font-medium ${PROV_STYLE[k] || "bg-slate-100"}`}>{p.label}</span></td>
                    <td className="text-right tabular-nums">{p.count}</td><td className="text-right tabular-nums">{p.tx}</td>
                    <td className="text-right tabular-nums">{chf(p.gross)}</td><td className="text-right tabular-nums text-rose-700">{chf(p.fees)}</td>
                    <td className="text-right tabular-nums">{pct(p.fee_pct)}</td><td className="text-right tabular-nums">{chf(p.net)}</td>
                  </tr>); })}
                {provKeys.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-slate-500 text-xs" data-testid="fees-empty">Keine Auszahlungsberichte für {data.year}. Lade TWINT-/SumUp-/Payrexx-Exporte im Posteingang hoch.</td></tr>}
              </tbody>
            </table>
          </Card>

          {data.months.length > 0 && (
            <Card className="p-4 border border-slate-200 bg-white" data-testid="fees-month-table">
              <h2 className="text-sm font-semibold text-slate-800 mb-3">Pro Monat</h2>
              <table className="w-full text-sm">
                <thead className="text-xs text-slate-500"><tr><th className="text-left py-1">Monat</th>{provKeys.map((k) => <th key={k} className="text-right">{data.providers[k].label} Geb.</th>)}<th className="text-right">Umsatz</th><th className="text-right">Gebühren</th><th className="text-right">Satz</th><th className="text-right">Konto {data.account.code}</th><th className="w-40"></th></tr></thead>
                <tbody>
                  {data.months.map((m) => (
                    <tr key={m.month} className="border-t border-slate-100" data-testid={`fees-month-${m.month}`}>
                      <td className="py-1.5 font-medium">{MONTHS[Number(m.month.slice(5, 7)) - 1]} {m.month.slice(0, 4)}</td>
                      {provKeys.map((k) => <td key={k} className="text-right tabular-nums text-slate-600">{m.providers[k] ? chf(m.providers[k].fees) : "–"}</td>)}
                      <td className="text-right tabular-nums">{chf(m.gross)}</td>
                      <td className="text-right tabular-nums text-rose-700 font-medium">{chf(m.fees)}</td>
                      <td className="text-right tabular-nums">{pct(m.fee_pct)}</td>
                      <td className="text-right tabular-nums text-slate-600" title="Alle auf Konto 6940 gebuchten Gebühren in diesem Monat">{chf(m.account_fees)}</td>
                      <td className="pl-3"><div className="h-2 rounded bg-rose-100"><div className="h-2 rounded bg-rose-500" style={{ width: `${Math.round((m.fees / maxFees) * 100)}%` }} /></div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
